import Link from 'next/link';
import Head from 'next/head';

import { PAGE_BLOG, PAGE_BLOG_POST } from '@root/constants/routes';
import { BlogDownstreamProps, formatPublishedAt, excerpt } from '@root/pages/wedding-ideas-blog';
import BlogPicture from 'components/Blog/Picture';
import Card from 'components/Card';
import CardScroll from 'components/CardScroll';
import IconArrowRight from 'components/IconArrowRight';
import NextSteps from 'components/NextSteps';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutInspiration from 'components/SalesCalloutInspiration';
import LayoutBlog from 'layouts/Blog';

const BlogIndex = ({ front, featured, primary, title, totalPages, updatePage }: BlogDownstreamProps) => (
  <>
    <Head>
      <title>{title ?? 'Menguin Blog'}</title>
    </Head>
    <div className="bg-gray-lighter py-1">
      <LayoutBlog>
        <section className="container lg:max-w-screen-md">
          <Link
            href={PAGE_BLOG_POST}
            as={`${PAGE_BLOG}/${primary.slug?.current}`}
            className="row group relative mb-64 grid-cols-1 bg-white shadow-3 hover:no-underline md:grid-cols-12"
          >
            <BlogPicture
              slot="indexHero"
              url={primary.mainImage.asset.url!}
              alt={`${primary.mainImage.alt} thumbnail`}
              className="col-span-1 md:col-span-8 md:aspect-ratio-7by5 lg:aspect-ratio-7by5"
              crop
              nativeWidth={primary.mainImage.asset.metadata?.dimensions?.width!}
            />
            <div className="col-span-1 flex flex-col justify-center p-32 md:col-span-4 md:pl-0">
              <span className="text-xs uppercase">{formatPublishedAt(primary.publishedAt ?? '')}</span>
              <h2 className="group-hover:text-gold-darker text-h2-display duration-150">{primary.title}</h2>

              <div className="group-hover:text-gold-darker py-16 text-gray-dark duration-150 sm:py-24">{excerpt(primary)}</div>

              <div>
                <div className="btn-info btn">
                  {'Read More'} <IconArrowRight />
                </div>
              </div>
            </div>
          </Link>
        </section>

        {featured.length > 0 && (
          <div className="m-section">
            <h2 className="text-h2 container uppercase lg:max-w-screen-md">Featured</h2>

            <CardScroll>
              {featured.map((post) => (
                <Card
                  as={`${PAGE_BLOG}/${post.slug?.current}`}
                  ctaTitle="Read More"
                  href={PAGE_BLOG_POST}
                  key={post._id}
                  title={post.title ?? 'Title Missing'}
                  trackerClass={`tracker-callout-blog-post_${post.slug?.current.replace(/-/g, '_')}-210920-134950`}
                  image={
                    <BlogPicture
                      alt={`${post.mainImage.alt}`}
                      className="aspect-ratio-16by9"
                      crop
                      nativeWidth={post.mainImage.asset.metadata?.dimensions?.width!}
                      slot="indexCard"
                      url={post.mainImage.asset.url!}
                    />
                  }
                  className="w-[280px]"
                >
                  {excerpt(post)}
                </Card>
              ))}
            </CardScroll>
          </div>
        )}

        {front.length > 0 && (
          <section className="m-section container lg:max-w-screen-md ">
            <h2 className="text-h2 mb-32 space-y-16 uppercase">Latest Posts</h2>

            <div className="space-y-32 md:space-y-64">
              {front.map((post) => (
                <Link
                  href={PAGE_BLOG_POST}
                  as={`${PAGE_BLOG}/${post.slug?.current}`}
                  key={post._id}
                  className={`row group col-span-12 col-start-1 grid-cols-1 space-y-16 bg-white duration-200 hover:no-underline hover:shadow-3 md:col-span-10 md:col-start-2 md:grid-cols-12 tracker-callout-blog-post_${post.slug?.current.replace(
                    /-/g,
                    '_',
                  )}-210920-134950 relative`}
                >
                  <BlogPicture
                    slot="indexHero"
                    url={post.mainImage.asset.url!}
                    alt={`${post.mainImage.alt} thumbnail`}
                    className="col-span-1 aspect-ratio-7by5 md:col-span-8"
                    crop
                    nativeWidth={post.mainImage.asset.metadata?.dimensions?.width!}
                  />
                  <div className="col-span-1 flex flex-col justify-center p-32 md:col-span-4 md:p-0 md:pr-32">
                    <span className="text-xs uppercase">{formatPublishedAt(post.publishedAt ?? '')}</span>
                    <h2 className="group-hover:text-gold-darker text-h2-display duration-150">{post.title}</h2>

                    <div className="group-hover:text-gold-darker py-16 text-gray-dark duration-150">{excerpt(post)}</div>

                    <div className="flex items-center">
                      <span className="text-gold-darker text-h5">Read More</span>
                      <IconArrowRight className="!text-gold-darker" />
                    </div>
                  </div>
                </Link>
              ))}

              <div className="col-span-12 col-start-1 flex items-center md:col-span-10 md:col-start-2">
                <div className="flex-grow"></div>
                <div className="text-sm">Page 1 of {totalPages}</div>
                <div className="flex-grow text-right">
                  <button className="btn-info btn" onClick={() => updatePage(2)}>
                    {'More Posts'} <IconArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}
      </LayoutBlog>
    </div>

    <NextSteps
      ctaPageName="blog"
      salesCallout1={<SalesCalloutSwatches ctaPageName="blog" />}
      salesCallout2={<SalesCalloutInspiration ctaPageName="blog" />}
    />
  </>
);

export default BlogIndex;
